import { mapDesigns } from "@mono/mapDesigns"
import { socket } from "@/utils/utils"

// The action cards whose target is picked directly on the board instead of
// from a list in PlayerCardsModal. Four of them target a property; the swap
// card targets a player, which to the server is a player order but to the
// player at the table is "the tile that opponent is standing on" — so it is
// picked the same way.
export const boardPickerCardIds: BoardPickerCardId[] = [
  "forced-auction",
  "force-downgrade-property",
  "purchase-any-property-for-double-price",
  "rent-increase-of-property",
  "swap-position-with-player",
]

export const isBoardPickerCardId = (
  cardId: ActionCardId
): cardId is BoardPickerCardId =>
  (boardPickerCardIds as string[]).includes(cardId)

// What a click on an eligible tile actually selects. Everything downstream —
// which tiles light up, what the banner says, what gets emitted — hangs off
// this one distinction.
export const getBoardPickerTargetKind = (
  cardId: BoardPickerCardId
): "property" | "player" =>
  cardId === "swap-position-with-player" ? "player" : "property"

// Shown as the board banner title while picking, so the player knows exactly
// what clicking a tile will do before they do it.
export const boardPickerTitles: Record<BoardPickerCardId, string> = {
  "forced-auction": "Choose a property to force auction",
  "force-downgrade-property": "Choose a property to destroy a house",
  "purchase-any-property-for-double-price":
    "Choose a property to buy at double price",
  "rent-increase-of-property":
    "Choose a property to increase rent for three turns",
  "swap-position-with-player": "Choose a player to swap places with",
}

// The opponents standing on a given tile — the swap card's real targets, and
// the reason a tile click can be ambiguous: several tokens share a tile often
// enough (jail, Go) that picking the first one silently would be wrong.
export const getOpponentsOnCell = (
  game: IGameDetails,
  cellOrder: number,
  playerOrder: PlayerOrder
): IPlayer[] =>
  game.situation?.players.filter(
    (p) =>
      p.order !== playerOrder && !p.isBankrupt && p.position === cellOrder
  ) || []

export const getBoardPickerEligibleOrders = (
  game: IGameDetails,
  cardId: BoardPickerCardId,
  playerOrder: PlayerOrder
): number[] => {
  const map = mapDesigns.find((m) => m.id === game.settings?.mapId)
  const situation = game.situation
  if (!map || !situation) return []

  // Swap position: every tile with at least one other player on it, whatever
  // the tile happens to be — opponents are just as likely to be sitting on
  // Go, jail or a chance tile as on a property.
  if (cardId === "swap-position-with-player") {
    return Array.from(
      new Set(
        situation.players
          .filter((p) => p.order !== playerOrder && !p.isBankrupt)
          .map((p) => p.position)
      )
    )
  }

  if (cardId === "force-downgrade-property") {
    const player = situation.players.find((p) => p.order === playerOrder)
    return map.cells
      .filter((cell) => cell.type === "property")
      .filter((cell) => {
        const houseData = situation.housesData.find(
          (h) => h.propertyOrder === cell.order
        )
        if (!houseData?.houses) return false
        return !player?.properties.includes(cell.order)
      })
      .map((cell) => cell.order)
  }

  if (
    cardId === "forced-auction" ||
    cardId === "purchase-any-property-for-double-price"
  ) {
    return map.cells
      .filter((cell) => cell.type === "property")
      .filter(
        (cell) => !situation.players.some((p) => p.properties.includes(cell.order))
      )
      .map((cell) => cell.order)
  }

  // rent-increase-of-property: any property the playing player already owns.
  const player = situation.players.find((p) => p.order === playerOrder)
  return player?.properties || []
}

export type BoardPickerTarget =
  | { kind: "property"; propertyOrder: number }
  | { kind: "player"; targetedPlayerOrder: PlayerOrder }

// Fires the same socket calls PlayerCardsModal used to fire once a target was
// picked in-modal — forced auction has its own event, the rest go through the
// generic action-card path.
export const playBoardPickerCard = (
  roomid: string,
  cardId: BoardPickerCardId,
  playerOrder: PlayerOrder,
  target: BoardPickerTarget
) => {
  if (cardId === "forced-auction" && target.kind === "property") {
    socket.emit("game.forcedAuction", {
      roomid,
      propertyOrder: target.propertyOrder,
      playerOrder,
    })
    return
  }
  socket.emit("game.playActionCard", {
    roomid,
    cardId,
    playerOrder,
    data:
      target.kind === "property"
        ? { targetedPropertyOrder: target.propertyOrder }
        : { targetedPlayerOrder: target.targetedPlayerOrder },
  })
}
